import React, { useMemo } from 'react'
import { Text, ResponsiveContainer } from '@theneatcompany/nui'
import { FormattedMessage, useIntl } from 'react-intl'

import Layout from '@/components/Layout/Layout'
import Card from '@/components/common/Card/Card'
import { useAccounts } from '@/components/Providers/AccountsProvider'
import { formatPrecision } from '@/utils/forms'
import Link from '@/components/common/Link'
import { PLAN_SKU, ROUTES, SUBSCRIPTION_STATUSES } from '@/constants'
import { formatDate } from '@/utils/date'
import StatusLabel from '@/components/common/StatusLabel/StatusLabel'
import { getNextBillDate, getTrialName } from '@/utils/plans'
import { getLatestOverdueDate } from '@/utils/invoice'
import ModularizedPlanCards from '@/components/common/ModularizedPlanCard/ModularizedPlanCards'

import styles from './overview.module.scss'

export default function OverviewPage() {
  const { account, user, subscriptions: [base], renewalPreview, invoices } = useAccounts()
  const infoList = {
    id: account.id,
    name: `${user.firstName} ${user.lastName}`,
    email: user.email,
    phone: user.phone || 'None',
  }

  const { formatMessage } = useIntl()

  const overdueDueDate = useMemo(() => getLatestOverdueDate(invoices), [invoices])

  const subscriptionList = useMemo(
    () => (base ? {
      status: <StatusLabel />,
      createdDate: formatDate(base.startDate),

      ...(overdueDueDate
        ? { dueOn: formatDate(overdueDueDate) }
        : getNextBillDate(base, renewalPreview)),
    } : {}),
    [base, overdueDueDate, renewalPreview],
  )

  return (
    <Layout.Content>
      <Layout.Title messageId="accountOverview" />
      <ResponsiveContainer size="xl">
        <Card className={styles.accountInfo}>
          <Card.Header messageId="account" />
          <Card.Body className={styles.body}>
            <Card.List>
              {Object.entries(infoList).map(([key, value]) => (
                <Card.ListElement key={key} value={value} labelKey={key} className={styles.listElement} />
              ))}
            </Card.List>
            <div className={styles.balance}>
              <Text type="display" size="small" color="foreground-secondary">
                <FormattedMessage id="currentBalance" />
              </Text>
              <Text type="display" size="large" color="tint-brand-primary">
                {`$${formatPrecision(user.balance, 2)}`}
              </Text>
              <Text>
                <FormattedMessage
                  id="accountStatus"
                  values={{
                    status: (
                      <Text key="status" typeStyle="bold" className={styles.statusLabel} color={overdueDueDate ? 'tint-danger-primary' : undefined}>
                        {formatMessage({ id: `accountBillingStatus.${account.billingStatus}` })}
                      </Text>
                    ),
                  }}
                />
              </Text>

              {overdueDueDate &&
              <div className={styles.actions}>
                <Link to={ROUTES.paymentMethod} variant="primary">
                  <FormattedMessage id="button.updatePaymentMethod" />
                </Link>
              </div>}
            </div>
          </Card.Body>
        </Card>
      </ResponsiveContainer>
      {base?.status === SUBSCRIPTION_STATUSES.EXPIRED ? (
        <ResponsiveContainer size="lg">
          <Card>
            <Card.Header messageId="resubscribe" />
            <Card.Body className={styles.resubscribe}>
              <ModularizedPlanCards isResubscribe />
            </Card.Body>
          </Card>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer size="sm">
          <Card className={styles.subscription}>
            <Card.Header messageId="subscription" />
            {base ? (
              <Card.Body>
                <Text
                  element="h3"
                  typeStyle="bold"
                  type="display"
                  size="small"
                  color="tint-brand-primary"
                  className={styles.planName}
                >
                  {getTrialName(base)}
                </Text>
                <Card>
                  <Card.Body>
                    <Card.List>
                      {Object.entries(subscriptionList).map(([key, value]) => (
                        <Card.ListElement key={key} value={value} labelKey={key} />
                      ))}
                    </Card.List>
                  </Card.Body>
                </Card>
                {base.status === SUBSCRIPTION_STATUSES.ACTIVE && account.transition?.activationDate && (
                  <div className={styles.trialMessage}>
                    <Text size="xsmall" color="foreground-secondary" element="p">
                      <FormattedMessage
                        id="futurePlanConvertOverview"
                        values={{
                          planName: account.transition?.name,
                          date: formatDate(account.transition?.activationDate),
                        }}
                      />
                    </Text>
                  </div>
                )}
                {(base.planSku === PLAN_SKU.NEAT_HOME ? base.status !== SUBSCRIPTION_STATUSES.EXPIRED : true) && (
                  <div className={styles.actions}>
                    <Link to={ROUTES.subscription} variant="primary">
                      <FormattedMessage id="button.manageSubscription" />
                    </Link>
                  </div>
                )}
              </Card.Body>
            ) : (
              <Card.Body>
                <Text color="tint-danger-primary" element="p">
                  <FormattedMessage id="noSubscriptions" />
                </Text>
              </Card.Body>
            )}
          </Card>
        </ResponsiveContainer>
      )}
    </Layout.Content>
  )
}
